<template>
  <div class="crm-settings">
    <div v-if="entity === 'clients'" class="card mb-4">
      <div class="card-body">
        <div class="row align-items-start g-3">
          <div class="col-md-7">
            <h6 class="card-title mb-1">
              <AppIcon name="bi-currency-exchange" class="bi bi-currency-exchange me-2" />
              {{ $t('client_invoice_currency') }}
            </h6>
            <p class="text-muted small mb-0">{{ $t('client_invoice_currency_description') }}</p>
          </div>
          <div class="col-md-5">
            <div class="form-check form-switch">
              <input
                id="client-invoice-currency-toggle"
                v-model="clientCurrencyEnabled"
                class="form-check-input"
                type="checkbox"
                :disabled="saving"
                @change="saveClientCurrencySetting"
              >
              <label class="form-check-label" for="client-invoice-currency-toggle">
                {{ clientCurrencyEnabled ? $t('enabled') : $t('disabled') }}
              </label>
            </div>
            <small class="text-warning d-block mt-2">
              <AppIcon name="bi-exclamation-triangle" class="bi bi-exclamation-triangle me-1" />
              {{ $t('client_currency_no_conversion') }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex align-items-start justify-content-between gap-3 mb-4">
          <div>
            <h6 class="card-title mb-1">
              <AppIcon name="bi-ui-checks-grid" class="bi bi-ui-checks-grid me-2" />
              {{ $t(formFieldsTitle) }}
            </h6>
            <p class="text-muted small mb-0">{{ $t(formFieldsDescription) }}</p>
          </div>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="saving" @click="resetFormFields">
            <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise me-1" />
            {{ $t('reset') }}
          </button>
        </div>

        <div class="row g-3">
          <div v-for="(isVisible, key) in formFields" :key="key" class="col-md-4 col-sm-6">
            <div class="form-check form-switch">
              <input
                :id="`${entity}-form-field-${key}`"
                v-model="formFields[key]"
                class="form-check-input"
                type="checkbox"
                :disabled="saving || key === 'name'"
                @change="saveFormFields"
              >
              <label class="form-check-label" :for="`${entity}-form-field-${key}`">
                {{ $t(getFormFieldLabel(key)) }}
                <AppIcon name="bi-lock-fill" v-if="key === 'name'" class="bi bi-lock-fill ms-1 text-muted" aria-hidden="true" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="d-flex align-items-start justify-content-between gap-3 mb-4">
          <div>
            <h6 class="card-title mb-1">
              <AppIcon name="bi-table" class="bi bi-table me-2" />
              {{ $t(tableTitle) }}
            </h6>
            <p class="text-muted small mb-0">{{ $t('table_columns_description') }}</p>
          </div>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="saving" @click="resetColumns">
            <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise me-1" />
            {{ $t('reset') }}
          </button>
        </div>

        <div class="row g-3">
          <div v-for="(isVisible, key) in visibleColumns" :key="key" class="col-md-4 col-sm-6">
            <div class="form-check form-switch">
              <input
                :id="`${entity}-column-${key}`"
                v-model="visibleColumns[key]"
                class="form-check-input"
                type="checkbox"
                :disabled="saving"
                @change="saveColumnSettings"
              >
              <label class="form-check-label" :for="`${entity}-column-${key}`">
                {{ $t(getColumnLabel(key)) }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper'
import {
  CRM_TABLE_COLUMN_LABELS,
  getCrmTableColumnDefaults
} from '../../constants/crmTableColumns'
import {
  CRM_CLIENT_FORM_FIELD_LABELS,
  getCrmClientFormFieldDefaults
} from '../../constants/crmClientFormFields'
import {
  CRM_SUPPLIER_FORM_FIELD_LABELS,
  getCrmSupplierFormFieldDefaults
} from '../../constants/crmSupplierFormFields'

export default {
  name: 'CrmSettings',
  props: {
    activeTab: {
      type: String,
      default: 'clients'
    }
  },
  data() {
    return {
      saving: false,
      clientCurrencyEnabled: false,
      formFields: getCrmClientFormFieldDefaults(),
      visibleColumns: getCrmTableColumnDefaults('clients')
    }
  },
  computed: {
    business() {
      return this.$store.getters.BUSINESS
    },
    entity() {
      return this.activeTab === 'suppliers' ? 'suppliers' : 'clients'
    },
    metadataKey() {
      return `crm_${this.entity}_columns`
    },
    tableTitle() {
      return this.entity === 'suppliers'
        ? 'crm_suppliers_table_columns'
        : 'crm_clients_table_columns'
    },
    formFieldsTitle() {
      return this.entity === 'suppliers' ? 'supplier_form_fields' : 'client_form_fields'
    },
    formFieldsDescription() {
      return this.entity === 'suppliers'
        ? 'supplier_form_fields_description'
        : 'client_form_fields_description'
    }
  },
  watch: {
    activeTab: {
      immediate: true,
      handler() {
        this.loadColumnSettings()
        this.loadFormFields()
        if (this.entity === 'clients') {
          this.loadClientCurrencySetting()
        }
      }
    }
  },
  methods: {
    getColumnLabel(key) {
      if (key === 'balance_due') {
        return this.entity === 'suppliers' ? 'supplier_balance_due' : 'client_balance_due'
      }

      return CRM_TABLE_COLUMN_LABELS[key] || key
    },
    getFormFieldLabel(key) {
      const labels = this.entity === 'suppliers'
        ? CRM_SUPPLIER_FORM_FIELD_LABELS
        : CRM_CLIENT_FORM_FIELD_LABELS
      return labels[key] || key
    },
    loadColumnSettings() {
      const savedColumns = this.business?.metadata?.[this.metadataKey] || {}
      this.visibleColumns = {
        ...getCrmTableColumnDefaults(this.entity),
        ...savedColumns
      }
    },
    loadClientCurrencySetting() {
      this.clientCurrencyEnabled = !!this.business?.metadata?.use_client_currency_for_invoices
    },
    loadFormFields() {
      const metadataKey = this.entity === 'suppliers'
        ? 'crm_supplier_form_fields'
        : 'crm_client_form_fields'
      const defaults = this.entity === 'suppliers'
        ? getCrmSupplierFormFieldDefaults()
        : getCrmClientFormFieldDefaults()
      const savedFields = this.business?.metadata?.[metadataKey] || {}
      this.formFields = {
        ...defaults,
        ...savedFields,
        name: true
      }
    },
    async saveClientCurrencySetting() {
      const previousValue = !!this.business?.metadata?.use_client_currency_for_invoices

      try {
        this.saving = true
        const metadata = {
          ...(this.business?.metadata || {}),
          use_client_currency_for_invoices: this.clientCurrencyEnabled
        }

        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata },
          { showToast: true }
        )

        this.$store.commit('SET_BUSINESS', {
          ...this.business,
          metadata
        })
      } catch (error) {
        this.$toast.error(error.message)
        this.clientCurrencyEnabled = previousValue
      } finally {
        this.saving = false
      }
    },
    async saveColumnSettings() {
      try {
        this.saving = true
        const metadata = {
          ...(this.business?.metadata || {}),
          [this.metadataKey]: { ...this.visibleColumns }
        }

        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata },
          { showToast: true }
        )

        this.$store.commit('SET_BUSINESS', {
          ...this.business,
          metadata
        })
      } catch (error) {
        this.$toast.error(error.message)
        this.loadColumnSettings()
      } finally {
        this.saving = false
      }
    },
    async saveFormFields() {
      const metadataKey = this.entity === 'suppliers'
        ? 'crm_supplier_form_fields'
        : 'crm_client_form_fields'
      const defaults = this.entity === 'suppliers'
        ? getCrmSupplierFormFieldDefaults()
        : getCrmClientFormFieldDefaults()
      const previousFields = this.business?.metadata?.[metadataKey] || defaults

      try {
        this.saving = true
        this.formFields.name = true
        const metadata = {
          ...(this.business?.metadata || {}),
          [metadataKey]: { ...this.formFields }
        }

        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata },
          { showToast: true }
        )

        this.$store.commit('SET_BUSINESS', {
          ...this.business,
          metadata
        })
      } catch (error) {
        this.$toast.error(error.message)
        this.formFields = {
          ...defaults,
          ...previousFields,
          name: true
        }
      } finally {
        this.saving = false
      }
    },
    async resetFormFields() {
      this.formFields = this.entity === 'suppliers'
        ? getCrmSupplierFormFieldDefaults()
        : getCrmClientFormFieldDefaults()
      await this.saveFormFields()
    },
    async resetColumns() {
      this.visibleColumns = getCrmTableColumnDefaults(this.entity)
      await this.saveColumnSettings()
    }
  }
}
</script>

<style scoped>
.crm-settings .card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.crm-settings .card-title {
  color: #333;
  font-size: 1rem;
  font-weight: 600;
}
</style>
